import axios from "axios";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { NoUserFound } from "../../../components/Utils";
import { OvalLoader } from "../../../components/common/Loader";
import MemberDetailsModal from "../../../components/common/MemberDetailsModal";
import { USER_EDIT } from "../../../routes/routes";

const getInitials = (name) => {
  const words = String(name || "")
    .replace(/[^A-Za-z ]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0]?.[0] || "?";
  const second = words[1]?.[0] || "";
  return `${first}${second}`.toUpperCase();
};

const RenderTableBlock = ({
  title,
  exportPerm,
  pdfKey,
  dataList,
  valueKey,
  valueLabel,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
  dateRangeText,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [detailsModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  const selectedUser = dataList?.[selectedIndex] || null;

  const handleViewProfile = async (userId) => {
    if (!userId) return;

    setDetailModal(true);
    setModalLoading(true);
    setDetailData({});

    try {
      const res = await axios.get(USER_EDIT + userId);
      if (res.status === 200 && res.data.success) {
        setDetailData(res.data.data);
      } else {
        toast.error(res.data.message || "Failed to load member details");
        setDetailModal(false);
      }
    } catch {
      toast.error("Failed to load member details");
      setDetailModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [title]);

  const memberName =
    selectedUser?.name ||
    selectedUser?.from_user_name ||
    selectedUser?.user_name ||
    "";
  const companyName =
    selectedUser?.user_company_name || selectedUser?.company_name || "N/A";
  const place = [selectedUser?.chapter_name, selectedUser?.region_name]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="top10-layout">
      <div className="top10-layout-table">
          <div className="modern-card-header">
            <div>
              <h5 className="d-flex align-items-center mb-0">
                <i
                  className="bx bxs-trophy me-2"
                  style={{
                    color: "#8e7c13ff",
                    fontSize: "1.5rem",
                    filter: "drop-shadow(0px 2px 2px rgba(255, 215, 0, 0.4))",
                  }}
                ></i>
                {title}
              </h5>
              {dateRangeText && (
                <div
                  style={{
                    fontSize: "0.825rem",
                    color: "#6c757d",
                    fontWeight: 400,
                    marginTop: "3px",
                  }}
                >
                  {dateRangeText}
                </div>
              )}
            </div>
            {hasPermission(exportPerm) && dataList?.length > 0 && (
              <button
                className="btn-modern-download"
                title="Download PDF"
                onClick={() => {
                  if (!pdfLoading) handleDownloadPDF(pdfKey);
                }}
                disabled={pdfLoading}
              >
                <i
                  className={`bx bx-cloud-download ${pdfLoading ? "bx-fade-down" : ""}`}
                ></i>
                Download PDF
              </button>
            )}
          </div>
          <div className="rich-list-wrapper">
            {loading ? (
              <div style={{ padding: "40px" }}>
                <OvalLoader />
              </div>
            ) : dataList?.length > 0 ? (
              dataList.map((users, i) => (
                <div
                  className={`rich-list-item${selectedIndex === i ? " is-selected" : ""}`}
                  key={i}
                  onClick={() => setSelectedIndex(i)}
                >
                  <span className={`top10-rank${i < 3 ? " is-top" : ""}`}>
                    {i + 1}
                  </span>
                  <span className="top10-initials">
                    {getInitials(users?.name)}
                  </span>
                  <div className="rich-item-info">
                    <span className="rich-item-name">{users?.name}</span>
                    <span className="rich-item-sub">
                      {users?.user_company_name ||
                        users?.company_name ||
                        "Member"}
                    </span>
                  </div>
                  <div className="rich-item-value">
                    <div className="top10-points">{users?.[valueKey] || 0}</div>
                    <div className="top10-points-unit">{valueLabel}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: "64px 24px", textAlign: "center" }}>
                <NoUserFound
                  title={`No ${title.replace("Top 10 ", "")} Found`}
                  subtitle="Everything is on track! There are no records to display at this time."
                />
              </div>
            )}
          </div>
      </div>
      <div className="top10-layout-card">
        {selectedUser && (
          <div className="top-performer-card">
            <div className="tp-head">
              <span className="tp-kicker">Top performer</span>
              {/* <span className="tp-rank">Rank {selectedIndex + 1}</span> */}
            </div>
            <div className="tp-identity">
              <span className="tp-avatar">{getInitials(memberName)}</span>
              <div className="tp-identity-text">
                <div className="tp-name">{memberName || "N/A"}</div>
                <div className="tp-place">{place || "N/A"}</div>
              </div>
            </div>
            <div className="tp-score">
              <span className="tp-score-num">
                {selectedUser[valueKey] || 0}
              </span>
              <span className="tp-score-label">{valueLabel}</span>
            </div>
            <div className="tp-details">
              <div>
                <div className="tp-label">Email</div>
                <div className="tp-value top10-email">
                  {selectedUser.email || "N/A"}
                </div>
              </div>
              <div>
                <div className="tp-label">Category</div>
                <div className="tp-value">
                  {selectedUser.category_name || "N/A"}
                </div>
              </div>
              <div>
                <div className="tp-label">Company</div>
                <div className="tp-value">{companyName}</div>
              </div>
            </div>
            <button
              className="btn-view-profile"
              onClick={(e) => {
                e.stopPropagation();
                handleViewProfile(
                  selectedUser.id ||
                    selectedUser.from_user_id ||
                    selectedUser.user_id,
                );
              }}
            >
              View Profile
            </button>
          </div>
        )}
      </div>

      <MemberDetailsModal
        show={detailsModal}
        onHide={() => setDetailModal(false)}
        detailData={detailData}
        loading={modalLoading}
      />
    </div>
  );
};

const TopTenTables = ({
  data,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
  dateRangeText,
}) => {
  const tabs = [
    {
      id: "points",
      label: "Points",
      icon: "bx-user",
      perm: "dashboard-points-data-view",
    },
    {
      id: "rfls",
      label: "RFLs Given",
      icon: "bx-transfer",
      perm: "dashboard-rfls-data-view",
    },
    {
      id: "tyns",
      label: "Business Given",
      icon: "bx-rupee",
      perm: "dashboard-tyns-data-view",
    },
    {
      id: "121s",
      label: "121s",
      icon: "bx-coffee",
      perm: "dashboard-121s-data-view",
    },
    {
      id: "visitors",
      label: "Visitors",
      icon: "bx-user-plus",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "chief_guests",
      label: "Chief Guests",
      icon: "bx-crown",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "inductions",
      label: "Inductees",
      icon: "bx-user-check",
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "power_dates",
      label: "Power Dates",
      icon: "bx-calendar",
      perm: "dashboard-visitors-data-view",
    },
  ];

  const tabConfig = {
    points: {
      title: "Top 10 Performing Members",
      exportPerm: "dashboard-points-data-view-export",
      pdfKey: "top_10_points_members",
      dataList: data.top10PointsUsersList,
      valueKey: "points",
      valueLabel: "Points",
      kingTitle: "King of Performers",
    },
    tyns: {
      title: "Top 10 Business Given Members",
      exportPerm: "dashboard-tyns-data-view-export",
      pdfKey: "top_10_business_given_members",
      dataList: data.top10BusinessUsersList,
      valueKey: "business_value",
      valueLabel: "Amount",
      kingTitle: "King of TYNs",
    },
    rfls: {
      title: "Top 10 RFLs Given Members",
      exportPerm: "dashboard-rfls-data-view-export",
      pdfKey: "top_10_referral_given_members",
      dataList: data.top10ReferralsGivenUsersList,
      valueKey: "referrals_count",
      valueLabel: "Count",
      kingTitle: "King of RFLs",
    },
    visitors: {
      title: "Top 10 Visitors Invited Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_visitors_members",
      dataList: data.top10VisitorsGivenUsersList,
      valueKey: "visitors_count",
      valueLabel: "Count",
      kingTitle: "King of Visitors",
    },
    "121s": {
      title: "Top 10 121s By Members",
      exportPerm: "dashboard-121s-data-view-export",
      pdfKey: "top_10_121_members",
      dataList: data.top10121sGivenUsersList,
      valueKey: "one2one_count",
      valueLabel: "Count",
      kingTitle: "King of 121s",
    },
    inductions: {
      title: "Top 10 Inductees Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_inductions_members",
      dataList: data.top10InductionsGivenUsersList,
      valueKey: "inductions_count",
      valueLabel: "Count",
      kingTitle: "King of Inductions",
    },
    power_dates: {
      title: "Top 10 Power Dates Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_power_dates_members",
      dataList: data.top10PowerDateGivenUsersList,
      valueKey: "power_dates_count",
      valueLabel: "Count",
      kingTitle: "King of Power Dates",
    },
    chief_guests: {
      title: "Top 10 Chief Guests Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_chief_guests_members",
      dataList: data.top10ChiefGuestGivenUsersList,
      valueKey: "chief_guests_count",
      valueLabel: "Count",
      kingTitle: "King of Chief Guests",
    },
  };

  const allowedTabs = tabs.filter((tab) => hasPermission(tab.perm));
  const [activeTab, setActiveTab] = useState(
    allowedTabs.length > 0 ? allowedTabs[0].id : "",
  );

  useEffect(() => {
    if (
      allowedTabs.length > 0 &&
      !allowedTabs.find((t) => t.id === activeTab)
    ) {
      setActiveTab(allowedTabs[0].id);
    }
  }, [hasPermission]);

  if (allowedTabs.length === 0) return null;

  const config = tabConfig[activeTab];

  return (
    <div className="row dash-table mb-5 pb-5">
      <div className="col-12">
        <div className="card modern-card mb-4 t10-shell-card">
          <div className="t10-shell">
            <nav className="t10-rail" aria-label="Top 10 categories">
              <span className="t10-rail-mark" aria-hidden="true">
                <i className="bx bx-trophy"></i>
              </span>
              {allowedTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`t10-rail-tab${
                    activeTab === tab.id ? " is-active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`bx ${tab.icon}`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>

            <div className="t10-main">
              {config && (
                <RenderTableBlock
                  {...config}
                  loading={loading}
                  hasPermission={hasPermission}
                  pdfLoading={pdfLoading}
                  handleDownloadPDF={handleDownloadPDF}
                  dateRangeText={dateRangeText}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopTenTables;
